import { CapacitorHttp } from '@capacitor/core';
import {
  CONTENT_TYPE,
  MAX_URL_LENGTH,
  PREFER,
  PREFER_RESPOND_ASYNC,
  X_ASW_FORCE,
  X_ASW_WARNING,
  X_HTTP_METHOD_OVERRIDE,
  X_SESSION_TOKEN,
} from '../../../constants';
import {
  HttpMethod,
  Headers,
  Body,
  BoundErrorResponse,
} from '../../types/types';
import { processFetcherResponse } from '../response/processFetcherResponse';
import { processJSONResponseErrors } from '../response/processJSONResponseErrors';
import { processErrorResponseTemplate } from '../response/processErrorResponseTemplate';
import { processPollLongRunningOperation } from '../response/processPollLongRunningOperation';
import { stringifyBracketParameter } from '../../../utils/apiRequests/stringifyBracketParameter';
import { JSONObject } from '../../../types/types';
import { LongRunningAbortedError } from '../../errors';
import { registerPoll, unregisterPoll } from '../response/pollAbortRegistry';
import { getHeaderValue } from '../response/getHeaderValue';
import { debugLogger } from '../../../debug/DebugLogger';

const logLongRunning = debugLogger.scope('longRunningOperations');
const logForce = debugLogger.scope('forceErrors');
const logRequests = debugLogger.scope('apiRequests');
const logServerWarnings = debugLogger.scope('serverWarnings');

export const fetcher = async <T>(
  url: string,
  method: HttpMethod,
  token: string | null,
  forceHeader?: string,
  params?: { [key: string]: string | string[] | JSONObject },
  body?: Body | string,
  headers?: Headers,
  returnHeaders?: boolean,
  errorResponse?: BoundErrorResponse,
  // Opt-in to long-running operation support (Prefer header + 202 polling).
  longRunning?: boolean
): Promise<T> => {
  let requestHeaders: Headers = {};

  if (token) {
    requestHeaders[X_SESSION_TOKEN] = token;
  }

  // Both force and entry error answers travel in this header (comma-chained).
  if (forceHeader) {
    requestHeaders[X_ASW_FORCE] = forceHeader;

    /** @debugFeature forceErrors Logs a request retried with the accumulated X-ASW-FORCE value and the answer-chain depth. Never logs other headers. */
    logForce('retrying with X-ASW-FORCE', () => ({
      url,
      method,
      force: forceHeader,
      chainDepth: forceHeader.split(',').length,
    }));
  }

  if (body) {
    requestHeaders[CONTENT_TYPE] = 'application/json';
  }
  if (headers) {
    requestHeaders = { ...requestHeaders, ...headers };
  }

  if (longRunning) {
    requestHeaders[PREFER] = PREFER_RESPOND_ASYNC;
  }

  // use URL object to avoid encoding issues; provide origin as base for relative URLs
  const isAbsolute = /^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(url);
  const newUrl = new URL(url, isAbsolute ? undefined : window.location.origin);
  const bracketParamsArray: string[] = [];

  if (params) {
    Object.keys(params).forEach((key) => {
      const query = params?.[key];

      // omit empty values
      if (query === undefined || query === null || query === '') {
        return;
      }

      //process object as bracket parameter
      if (typeof query === 'object' && !Array.isArray(query)) {
        const stringifiedParam = stringifyBracketParameter({ [key]: query });
        bracketParamsArray.push(stringifiedParam);
        return;
      }

      if (Array.isArray(query)) {
        newUrl.searchParams.append(key, query.join(','));
        return;
      }

      newUrl.searchParams.append(key, query);
    });
  }

  // Preserve relative URL format when input was relative (no scheme)
  let fullUrl = isAbsolute
    ? newUrl.toString()
    : `${newUrl.pathname}${newUrl.search}`;

  // unable to process by append method
  bracketParamsArray.forEach((param) => {
    if (fullUrl.includes('?')) {
      fullUrl = `${fullUrl}&${param}`;
      return;
    }

    fullUrl = `${fullUrl}?${param}`;
  });

  let requestMethod: HttpMethod = method;
  let requestUrl = fullUrl;
  let requestData: any = body ? JSON.stringify(body) : undefined;

  if (requestMethod === 'GET' && fullUrl.length > MAX_URL_LENGTH) {
    const qIdx = fullUrl.indexOf('?');
    if (qIdx !== -1) {
      requestUrl = fullUrl.slice(0, qIdx);
      requestData = fullUrl.slice(qIdx + 1);
      requestMethod = 'POST';
      requestHeaders[X_HTTP_METHOD_OVERRIDE] = 'GET';
      requestHeaders[CONTENT_TYPE] = 'application/x-www-form-urlencoded';

      /** @debugFeature apiRequests Logs a GET silently rewritten to POST with X-HTTP-Method-Override because the url exceeded the length limit. */
      logRequests('GET rewritten to POST, url too long', () => ({
        url: requestUrl,
        urlLength: fullUrl.length,
        maxUrlLength: MAX_URL_LENGTH,
      }));
    }
  }

  // Register the poll controller BEFORE firing the initial request, so a
  // navigation that happens while that first request is still in flight is
  // recorded on the signal and cancels the poll loop that would otherwise start
  // once the (now unwanted) 202 response arrives.
  const pollController = longRunning ? registerPoll() : undefined;

  /** @debugFeature apiRequests Logs every fired API request: method, url and flags (body, force retry, long-running). Bodies and headers are not logged — the session token must never appear. */
  logRequests('request fired', () => ({
    method: requestMethod,
    url: requestUrl,
    hasBody: Boolean(body),
    isForceRetry: Boolean(forceHeader),
    longRunning: Boolean(longRunning),
  }));

  return (
    CapacitorHttp.request({
      url: requestUrl,
      method: requestMethod,
      headers: requestHeaders,
      data: requestData,
    })
      .then((response) => {
        /** @debugFeature apiRequests Logs every received API response: status and url. Mainly for mobile builds, where native requests bypass the DevTools network tab. */
        logRequests('response received', () => ({
          status: response.status,
          method: requestMethod,
          url: requestUrl,
        }));

        return processErrorResponseTemplate(response, errorResponse);
      })
      .then((response) =>
        processPollLongRunningOperation(
          response,
          longRunning,
          requestHeaders,
          pollController?.signal
        )
      )
      // Runs after polling so warnings of the final long-running response
      // are logged, not the initial 202's. Logs the original method and the
      // full url on purpose — unlike the apiRequests logs above, the query
      // that triggered the warning matters more than the wire-level POST
      // override.
      .then((response) => {
        if (debugLogger.isEnabled('serverWarnings')) {
          const warning = getHeaderValue(response.headers, X_ASW_WARNING);

          if (warning) {
            /** @debugFeature serverWarnings Logs an SQL developer warning returned in the X-ASW-Warning response header, together with the method and url that returned it. */
            logServerWarnings('X-ASW-Warning received', () => ({
              method,
              url: fullUrl,
              warning,
            }));
          }
        }

        return response;
      })
      .then((response) => processFetcherResponse(response, forceHeader))
      // process json errors
      .then((response) =>
        processJSONResponseErrors(response, returnHeaders, forceHeader)
      )
      // The poll loop was aborted because the user navigated away — this data
      // is no longer wanted. Swallow it here so no caller (hook or form) runs
      // its error path: return a never-settling promise, leaving every awaiting
      // continuation suspended instead of surfacing a toast/error/force modal.
      .catch((error) => {
        if (error instanceof LongRunningAbortedError) {
          /** @debugFeature longRunningOperations Logs a long-running request whose poll was aborted — its result is dropped and the awaiting caller never resumes (no toast, no error). */
          logLongRunning('aborted long-running result dropped', () => ({
            url: requestUrl,
            method: requestMethod,
          }));
          return new Promise<never>(() => undefined);
        }
        throw error;
      })
      .finally(() => {
        if (pollController) {
          unregisterPoll(pollController);
        }
      })
  );
};
